import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { checkinApi } from '@/services/api';
import { useAuth } from '@/context/AuthContext';

export function useGymCheckins() {
  const queryClient = useQueryClient();
  const { user, isGuestMode, loading: authLoading } = useAuth();
  
  // Get today's date in user's timezone but formatted consistently
  const getTodayDate = useCallback(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);
  
  // Memoize the enabled condition to prevent unnecessary re-renders
  const isEnabled = useMemo(() => {
    return !authLoading && (!!user || isGuestMode);
  }, [authLoading, user, isGuestMode]);
  
  // Query for fetching checkins (keep for history/dashboard use)
  const {
    data: checkins = [],
    isLoading: checkinsLoading,
    error: checkinsError,
    refetch: refetchCheckins
  } = useQuery({
    queryKey: ['gym-checkins'],
    queryFn: async () => {
      const result = await checkinApi.getCheckins();
      if (result.success) {
        return result.data || [];
      }
      throw new Error(result.error || 'Failed to fetch check-ins');
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    retry: 2, // Limit retries to prevent infinite loops
    enabled: isEnabled,
  });

  // Optimized query for check-in status - Backend as single source of truth
  const {
    data: checkinStatus,
    isLoading: statusLoading,
    error: statusError,
    refetch: refetchStatus
  } = useQuery({
    queryKey: ['checkin-status'],
    queryFn: async () => {
      const result = await checkinApi.getCheckinStatus();
      if (result.success) {
        return result.data;
      }
      throw new Error(result.error || 'Failed to fetch check-in status');
    },
    staleTime: 30 * 1000, // 30 seconds - frequent updates for check-in status
    refetchOnWindowFocus: false,
    retry: 2,
    enabled: isEnabled,
  });

    // Mutation for creating checkin
    const createCheckinMutation = useMutation({
      mutationFn: async (userCallback?: () => void) => {
        const result = await checkinApi.createCheckin();
        if (result.success) {
          return { data: result.data, userCallback };
        }
        throw new Error(result.error || 'Failed to check in');
      },
      onSuccess: async ({ userCallback }) => {
        // Invalidate both checkins and status queries
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['gym-checkins'] }),
          queryClient.invalidateQueries({ queryKey: ['checkin-status'] })
        ]);
        
        // Also refresh commitment status (auto-completion after check-in)
        await queryClient.invalidateQueries({ queryKey: ['gym-commitment-today'] });
        
        // Now call the callback - data should be fresh
        if (userCallback) {
          userCallback();
        }
      },
      onError: () => {
        // On error, ensure cache is refreshed
        queryClient.invalidateQueries({ queryKey: ['gym-checkins'] });
        queryClient.invalidateQueries({ queryKey: ['checkin-status'] });
      },
    });

  const createCheckin = useCallback(async (onSuccess?: () => void) => {
    try {
      await createCheckinMutation.mutateAsync(onSuccess);
      return true;
    } catch (error) {
      console.error('Failed to create check-in:', error);
      return false;
    }
  }, [createCheckinMutation]);

  // For testing - delete today's checkin
  const deleteTodaysCheckinMutation = useMutation({
    mutationFn: async () => {
      const result = await checkinApi.deleteTodaysCheckin();
      if (result.success) {
        return result.data;
      }
      throw new Error(result.error || 'Failed to delete check-in');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gym-checkins'] });
      queryClient.invalidateQueries({ queryKey: ['checkin-status'] });
    },
  });

  const deleteTodaysCheckin = useCallback(async () => {
    try {
      await deleteTodaysCheckinMutation.mutateAsync();
      return true;
    } catch (error) {
      console.error('Failed to delete check-in:', error);
      return false;
    }
  }, [deleteTodaysCheckinMutation]);

  // Backend as single source of truth - removes frontend complexity
  const isCheckedInWithin6Hours = useCallback(() => {
    return checkinStatus?.isCheckedIn || false;
  }, [checkinStatus]);

  // Legacy function name for backward compatibility
  const isCheckedInToday = useCallback(() => {
    return checkinStatus?.isCheckedIn || false;
  }, [checkinStatus])

  const getGymStreak = useCallback(() => {
    if (checkins.length === 0) return 0;

    const today = new Date();
    const uniqueDates = [...new Set(checkins.map(c => c.date))].sort().reverse();
    
    let streak = 0;
    for (let i = 0; i < uniqueDates.length; i++) {
      const checkinDate = new Date(uniqueDates[i] + 'T00:00:00'); // Ensure local timezone
      const daysDiff = Math.floor((today.getTime() - checkinDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === i) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  }, [checkins, getTodayDate]);

  const getMonthlyCheckins = useCallback(() => {
    const thisMonth = getTodayDate().slice(0, 7); // YYYY-MM from local timezone
    return checkins.filter(checkin => checkin.date.startsWith(thisMonth)).length;
  }, [checkins, getTodayDate]);

  // Get info about the latest check-in for UI feedback
  const getLatestCheckinInfo = useCallback(() => {
    if (!checkinStatus?.checkinInfo) return null;
    
    const latest = checkinStatus.checkinInfo;
    const checkinTime = new Date(latest.timestamp);
    const sixHoursAgo = new Date(Date.now() - 6 * 60 * 60 * 1000);
    
    const isWithinGracePeriod = checkinTime > sixHoursAgo;
    const nextAllowedTime = new Date(checkinTime.getTime() + 6 * 60 * 60 * 1000);
    const hoursUntilNext = Math.ceil((nextAllowedTime.getTime() - Date.now()) / (1000 * 60 * 60));
    
    return {
      timestamp: latest.timestamp,
      date: latest.date,
      isWithinGracePeriod,
      nextAllowedTime: nextAllowedTime.toISOString(),
      hoursUntilNext: Math.max(0, hoursUntilNext)
    };
  }, [checkinStatus]);

  const error = checkinsError?.message || statusError?.message || createCheckinMutation.error?.message || null;
  const loading = checkinsLoading || statusLoading;

  const refetch = useCallback(async () => {
    await Promise.all([refetchCheckins(), refetchStatus()]);
  }, [refetchCheckins, refetchStatus]);

  return {
    checkins,
    loading,
    error,
    createCheckin,
    deleteTodaysCheckin,
    isCheckedInToday,
    isCheckedInWithin6Hours,
    getLatestCheckinInfo,
    getGymStreak,
    getMonthlyCheckins,
    refetch,
    isCreatingCheckin: createCheckinMutation.isPending,
    isDeletingCheckin: deleteTodaysCheckinMutation.isPending,
  };
}